import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useParams, useHistory } from "react-router-dom";
import api from "../api";
import BottomNavBar from "../landingPageNew/BottomNavBar";
import { ArrowLeft } from "lucide-react";

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px 120px;
  font-family: "Quicksand", sans-serif;
  min-height: 100vh;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #8747d4;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  font-family: "Quicksand", sans-serif;
  padding: 0;
  margin-bottom: 24px;

  &:hover {
    opacity: 0.8;
  }
`;

const AnnouncementCard = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  margin-bottom: 32px;
`;

const AnnouncementHeader = styled.div`
  padding: 24px;
  background: linear-gradient(135deg, #8747d4 0%, #a855f7 100%);
  text-align: center;
`;

const Icon = styled.div`
  font-size: 40px;
  margin-bottom: 12px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 8px 0;
  font-family: "Quicksand", sans-serif;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  font-family: "Quicksand", sans-serif;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
`;

const DateText = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  margin: 8px 0 0 0;
  font-family: "Quicksand", sans-serif;
`;

const AnnouncementBody = styled.div`
  padding: 24px;
  font-size: 15px;
  line-height: 1.6;
  color: #333;
  font-family: "Quicksand", sans-serif;

  p {
    margin-bottom: 10px;
  }

  h1, h2, h3 {
    margin-top: 20px;
    margin-bottom: 10px;
    font-weight: 700;
  }

  ul, ol {
    margin: 12px 0;
    padding-left: 24px;
  }

  a {
    color: #8747d4;
  }

  img {
    max-width: 100%;
    border-radius: 8px;
  }
`;

const RepliesSection = styled.div`
  margin-top: 8px;
`;

const RepliesHeader = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #333;
  margin: 0 0 16px 0;
  font-family: "Quicksand", sans-serif;
`;

const ReplyCard = styled.div`
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 10px;
`;

const ReplyMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const ReplyEmail = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #8747d4;
  font-family: "Quicksand", sans-serif;
`;

const ReplyDate = styled.span`
  font-size: 12px;
  color: #999;
  font-family: "Quicksand", sans-serif;
`;

const ReplyMessage = styled.p`
  margin: 0;
  font-size: 14px;
  color: #444;
  line-height: 1.5;
  font-family: "Quicksand", sans-serif;
  white-space: pre-wrap;
`;

const EmptyReplies = styled.div`
  text-align: center;
  padding: 32px 16px;
  color: #999;
  font-size: 14px;
  font-family: "Quicksand", sans-serif;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-size: 16px;
  color: #999;
  font-family: "Quicksand", sans-serif;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-size: 16px;
  color: #d14343;
  font-family: "Quicksand", sans-serif;
`;

const SalaamSentDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const adminEmail = localStorage.getItem("userEmail");
  const [announcement, setAnnouncement] = useState(null);
  const [replies, setReplies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!adminEmail || !id) {
        setError("Invalid request");
        setIsLoading(false);
        return;
      }

      try {
        const [annRes, repliesRes] = await Promise.all([
          api.get("/api/announcements/admin/all", { params: { email: adminEmail } }),
          api.get(`/api/announcements/${id}/replies`, { params: { email: adminEmail } }),
        ]);

        const found = (annRes.data || []).find((a) => a._id === id);
        if (!found) {
          setError("Announcement not found");
        } else {
          setAnnouncement(found);
          setReplies(repliesRes.data || []);
        }
      } catch (err) {
        console.error("Failed to load announcement detail:", err);
        setError("Failed to load data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id, adminEmail]);

  if (isLoading) {
    return (
      <>
        <Container>
          <LoadingState>Loading...</LoadingState>
        </Container>
        <BottomNavBar />
      </>
    );
  }

  if (error || !announcement) {
    return (
      <>
        <Container>
          <ErrorState>{error || "Announcement not found"}</ErrorState>
        </Container>
        <BottomNavBar />
      </>
    );
  }

  return (
    <>
      <Container>
        <BackButton onClick={() => history.push("/announcement-admin/sent")}>
          <ArrowLeft size={16} /> Back to All Salaams
        </BackButton>

        <AnnouncementCard>
          <AnnouncementHeader>
            <Icon>{announcement.icon || "📢"}</Icon>
            <Title>{announcement.title}</Title>
            <MetaRow>
              {announcement.allowReplies && <Badge>Replies enabled</Badge>}
              {announcement.targetEmails && announcement.targetEmails.length > 0 ? (
                <Badge>{announcement.targetEmails.length} targeted</Badge>
              ) : (
                <Badge>Broadcast</Badge>
              )}
              <Badge>{(announcement.readBy || []).length} read</Badge>
            </MetaRow>
            {announcement.createdAt && (
              <DateText>
                {new Date(announcement.createdAt).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </DateText>
            )}
          </AnnouncementHeader>

          <AnnouncementBody
            dangerouslySetInnerHTML={{ __html: announcement.message }}
          />
        </AnnouncementCard>

        <RepliesSection>
          <RepliesHeader>
            Replies ({replies.length})
          </RepliesHeader>

          {replies.length === 0 ? (
            <EmptyReplies>
              {announcement.allowReplies
                ? "No replies yet."
                : "Replies were not enabled for this message."}
            </EmptyReplies>
          ) : (
            replies.map((reply) => (
              <ReplyCard key={reply._id}>
                <ReplyMeta>
                  <ReplyEmail>{reply.userEmail}</ReplyEmail>
                  <ReplyDate>
                    {reply.createdAt
                      ? new Date(reply.createdAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })
                      : ""}
                  </ReplyDate>
                </ReplyMeta>
                <ReplyMessage>{reply.message}</ReplyMessage>
              </ReplyCard>
            ))
          )}
        </RepliesSection>
      </Container>
      <BottomNavBar />
    </>
  );
};

export default SalaamSentDetail;
